import React from 'react'
import { Navigate, Outlet, RouteObject, useRoutes } from 'react-router-dom'
import AppUsersDetails from './components/AppUsers/Details'
import Experiences from './components/AppUsers/Experiences'
import AuthGuard from './components/auth/AuthGuard'
import GuestGuard from './components/auth/GuestGuard'
import DashboardLayout from './layouts/DashboardLayout'
import SplitAuthLayout from './layouts/SplitAuthLayout'
import AppUser from './pages/AppUser'
import AppUsers from './pages/AppUsers'
import BuzzlUsers from './pages/BuzzlUsers'
import NotFound from './pages/NotFound'
import SignIn from './pages/SignIn'
import Analytics from './pages/Analytics'
import ExperiencesPage from './pages/Experiences'
import Experience from './pages/Experience'
import Customers from './pages/Customers'
import Customer from './pages/Customer'
import CustomerDetails from './components/Customers/Details'
import CustomerExperiences from './components/Customers/Experiences'
import CustomerUsers from './components/Customers/Users'
import LocationSingle from './pages/LocationSingle'
import Locations from './pages/Locations'
import CustomerLocations from './components/Customers/Locations'
import DetailsView from './components/LocationSingle/DetailsView'
import Offers from './pages/Offers'
import LocationExperiencesView from './components/LocationSingle/ExperiencesView'
import NewOffer from './pages/NewOffer'
import OffersView from './components/LocationSingle/OffersView'
import BrandDetails from './components/BrandDetails/BrandDetails'
import Users from './pages/Users'
import Chat from './pages/Chat'
import ResetPassword from './pages/ResetPassword'

interface RouteWrapperProps {
  guard: React.FC | undefined
  layout: React.FC | undefined
}

const RouteWrapper = ({ guard, layout }: RouteWrapperProps) => {
  const Guard = guard ?? React.Fragment
  const Layout = layout ?? React.Fragment
  return (
    <Guard>
      <Layout>
        <Outlet />
      </Layout>
    </Guard>
  )
}

const routesConfig: RouteObject[] = [
  {
    path: '/',
    element: <RouteWrapper guard={AuthGuard} layout={DashboardLayout} />,
    children: [
      {
        index: true,
        element: <Navigate to="/experiences" />,
      },
      {
        path: '/app-users',
        element: <AppUsers />,
      },
      {
        path: '/users',
        element: <Users />,
      },
      {
        path: '/app-users/:id',
        element: <AppUser />,
        children: [
          {
            index: true,
            element: <AppUsersDetails />,
          },
          {
            path: '/app-users/:id/experiences',
            element: <Experiences />,
          },
        ],
      },
      {
        path: '/buzzl-users',
        element: <BuzzlUsers />,
      },
      {
        path: '/analytics',
        element: <Analytics />,
      },
      {
        path: '/experiences/:experienceId',
        element: <Experience />,
      },
      {
        path: '/rewards-and-offers',
        element: <Offers />,
      },
      {
        path: '/rewards-and-offers/add-new',
        element: <NewOffer />,
      },
      {
        path: '/rewards-and-offers/:id',
        element: <NewOffer />,
      },
      {
        path: '/customers/add-new',
        element: <Customer />,
        children: [
          {
            index: true,
            element: <CustomerDetails />,
          },
        ],
      },
      {
        path: '/customers/:customerId',
        element: <Customer />,
        children: [
          {
            index: true,
            element: <CustomerDetails />,
          },
          {
            path: '/customers/:customerId/experiences',
            element: <CustomerExperiences />,
          },
          {
            path: '/customers/:customerId/users',
            element: <CustomerUsers />,
          },
          {
            path: '/customers/:customerId/locations',
            element: <CustomerLocations />,
          },
        ],
      },
      {
        path: '/experiences',
        element: <ExperiencesPage />,
      },
      { path: '/customers', element: <Customers /> },
      { path: '/locations', element: <Locations /> },
      { path: '/brand-details', element: <BrandDetails /> },
      {
        path: '/locations/:id',
        element: <LocationSingle />,
        children: [
          {
            index: true,
            path: '/locations/:id',
            element: <DetailsView />,
          },
          {
            path: '/locations/:id/experiences',
            element: <LocationExperiencesView />,
          },
          {
            path: '/locations/:id/rewards',
            element: <OffersView />,
          },
        ],
      },
      {
        path: '/chat',
        element: <Chat />,
      },
    ],
  },
  {
    path: '/auth',
    element: <RouteWrapper guard={GuestGuard} layout={SplitAuthLayout} />,
    children: [
      {
        path: '/auth/login',
        element: <SignIn />,
      },
      {
        path: '/auth/reset-password',
        element: <ResetPassword />,
      },
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
]

const Routes = () => {
  const routing = useRoutes(routesConfig)

  return routing
}

export default Routes
